import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { CartItem } from '../interfaces/cart-item';
import { BehaviorSubject, forkJoin, Observable, of, Subject, timer } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { ArticoloFull } from '../interfaces/custom/articolo';
import { CartArticoloOption } from '../interfaces/custom/cart_articolo_option';
import { Destinazione } from '../interfaces/custom/destinazione';
import { PriceArticoloService } from './custom/price-articolo.service';
import { getHtmlTagDefinition } from '@angular/compiler';
import { Sconto } from '../interfaces/custom/sconto';
import { AccountService } from './custom/account.service';
import { ShopService } from '../api/shop.service';
import { Livello } from '../interfaces/custom/componibili';
import { StoreService } from './store.service';
import { Attributo } from '../interfaces/custom/attributi';
import { convertDateRitiroForDocumento, formattedCreateDate, localISOTime } from '../helpers/formatDate';
import { attributiMakeRows, createCorpoForBox } from 'src/app/shared/helpers/box_calc';
import { PayCondition } from '../interfaces/custom/pay_condition';
import { getTipoSpedizioneStr } from '../interfaces/custom/spedizione';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';

interface CartTotal {
    title: string;
    price: number;
    type: 'shipping' | 'fee' | 'tax' | 'other';
}

interface CartData {
    items: CartItem[];
    quantity: number;
    subtotal: number;
    shippingMethod: any;
    shippingCost: number;
    total: number;
    destination: Destinazione,
    deliveryDate: string,
    deliveryTime: any,
    ritiroTime: string,
    note: string,
    sconto: Sconto,
    scontoValue: number,
    payMethod: PayCondition,
    payMethodPrice: number
}

@Injectable({
    providedIn: 'root'
})
export class CartService {
    private data: CartData = {
        items: [],
        quantity: 0,
        subtotal: 0,
        shippingMethod: null,
        shippingCost: 0,
        total: 0,
        destination: null,
        deliveryDate: null,
        deliveryTime: null,
        ritiroTime: null,
        note: null,
        sconto: null,
        scontoValue: 0,
        payMethod: null,
        payMethodPrice: 0,
    };
    private itemsSubject$: BehaviorSubject<CartItem[]> = new BehaviorSubject(this.data.items);
    private quantitySubject$: BehaviorSubject<number> = new BehaviorSubject(this.data.quantity);
    private subtotalSubject$: BehaviorSubject<number> = new BehaviorSubject(this.data.subtotal);
    private totalSubject$: BehaviorSubject<number> = new BehaviorSubject(this.data.total);
    private onAddingSubject$: Subject<any> = new Subject();

    isEmpty() {
        return !this.data.items.length;
    }

    setItems(items) {
        this.data.items = items;
        this.save();
        this.calc();
    }

    get items(): ReadonlyArray<CartItem> {
        return this.data.items;
    }

    get quantity(): number {
        return this.data.quantity;
    }

    get subtotal(): number {
        return this.data.subtotal;
    }

    get shippingCost(): number {
        return this.data.shippingCost;
    }

    get shippingMethod() {
        return this.data.shippingMethod;
    }

    get destination() {
        return this.data.destination;
    }

    get deliveryDate() {
        return this.data.deliveryDate;
    }

    get deliveryTime() {
        return this.data.deliveryTime;
    }

    get ritiroTime() {
        return this.data.ritiroTime;
    }

    get note() {
        return this.data.note;
    }

    get sconto() {
        return this.data.sconto;
    }

    get scontoValue() {
        return this.data.scontoValue;
    }
    //product data variables
    readonly items$: Observable<CartItem[]> = this.itemsSubject$.asObservable();
    readonly quantity$: Observable<number> = this.quantitySubject$.asObservable();
    readonly subtotal$: Observable<number> = this.subtotalSubject$.asObservable();
    readonly total$: Observable<number> = this.totalSubject$.asObservable();

    readonly onAdding$: Observable<any> = this.onAddingSubject$.asObservable();

    constructor(
        @Inject(PLATFORM_ID)
        private platformId: any,
        private priceArticolo: PriceArticoloService,
        private account : AccountService,
        private shop : ShopService,
        public store : StoreService,
        private toastr : ToastrService
    ) {
        if (isPlatformBrowser(this.platformId)) {
            this.load();
            this.calc();
        }

        this.account.user$.subscribe(newUser => {
            // servizio salvataggio dati carta
            this.loadSavedCartsOnNewUser();

            let queries = {};
            for (let item of this.data.items) {
                queries[item.product.codart] = this.shop.getArticolo(item.product.codart).pipe(
                    catchError(e => of(null))
                )
            }

            if (!Object.keys(queries).length)
                return;

            forkJoin(queries).subscribe((response:any) => {
                for (let item of this.data.items) {
                    if (!response[item.product.codart])
                        continue;
                    try {
                        let oldImgUrl = item.product.cdn_url;
                        Object.assign(item.product, response[item.product.codart]);
                        item.product.cdn_url = oldImgUrl;
                        let options = {};
                        if (item.options) {
                            for (let i = 1; i <= 4; i++) {
                                if (item.options[i])
                                    options[i] = item.options[i].option.progressivo;
                            }
                            if (Object.keys(options).length == 0)
                                options = null;
                        }
                        item.price = this.priceArticolo.prezzoArticolo(response[item.product.codart], options);
                    } catch (err) {}
                }

                let itemsToRemove = this.data.items.filter(item => !response[item.product.codart] || item.price == -1);
                this.data.items = this.data.items.filter(item => response[item.product.codart] && item.price != -1);

                this.showItemsRemovedError(itemsToRemove);

                this.calc();
                this.save();
            });

        });

    }

    updateGiacenza(codart_articolo_map) {
        let report = {
            'removed': [],
            'changed': []
        };

        let quantita_articoli_con_option = {};

        let change_quantity = (item, newQuantity) => {
            report.changed.push({
                'product': item.product,
                'options': item.options,
                'oldQuantity': item.quantity,
                'newQuantity': newQuantity
            });
            item.quantity = newQuantity;
        }

        let get_giacenza_template = (item) => {
            let giacenza = {};
            const STR_OPT_LIST = "option_list";
            const STR_OPT_CODE = "option_code";
            for (let i = 1; i <= 4; i++) {
                giacenza[STR_OPT_LIST + i] = 0;
                giacenza[STR_OPT_CODE + i] = 0;
                if (item.options[i] && item.options[i].list.indmagazzino) {
                    giacenza[STR_OPT_LIST + i] = item.options[i].list.option_list;
                    giacenza[STR_OPT_CODE + i] = item.options[i].option.progressivo;
                }
            }
            return giacenza;
        }
        
        let idsToRemove = [];
        for (let i = 0; i < this.data.items.length; i++) {
            let item = this.data.items[i];
            let codart = item.product.codart;
            let articoloUpdate = codart_articolo_map[codart];
            if (!articoloUpdate) {
                idsToRemove.push(i);
            }
            else {
                if (!articoloUpdate.acquisti_giacenza_negativa) {
                    if (!item.options) {
                        let giacenza = Math.max(articoloUpdate.giacenze.giacenza.giacenza, 0);
                        if (giacenza < 1) {
                            idsToRemove.push(i);
                        }
                        else if (giacenza < item.quantity) {
                            change_quantity(item, giacenza);
                        }
                    }
                    else {
                        let giacenza_template = get_giacenza_template(item);

                        let totalGiacenza = 0;
                        let giacenzaObj = articoloUpdate.giacenze.giacenza_opt.find(obj => {
                            for (let key of Object.keys(giacenza_template)) {
                                if (obj[key] != giacenza_template[key])
                                    return false;
                            }
                            return true;
                        });

                        if (giacenzaObj)
                            totalGiacenza = giacenzaObj.giacenza;

                        let quantita_articolo = quantita_articoli_con_option[articoloUpdate.codart];
                        if (!quantita_articolo) {
                            quantita_articolo = this.getQuantityOfItemWithOptions(articoloUpdate);
                            quantita_articoli_con_option[articoloUpdate.codart] = quantita_articolo;
                        }

                        let quantitaObj = quantita_articolo.find(obj => {
                            for (let key of Object.keys(giacenza_template)) {
                                if (obj[key] != giacenza_template[key])
                                    return false;
                            }
                            return true;
                        });

                        let totalQuantity = quantitaObj.giacenza;

                        if (totalGiacenza < 1) {
                            idsToRemove.push(i);
                        }
                        else if (totalQuantity > totalGiacenza) {
                            if (totalQuantity == item.quantity) {
                                change_quantity(item, totalGiacenza);
                            }
                            else {
                                idsToRemove.push(i);
                            } 
                        }
                    }
                }

                item.product = articoloUpdate;
            }
        }

        for (let i = idsToRemove.length - 1; i >= 0; i--) {
            let idx = idsToRemove[i];
            report.removed.push({
                'product': this.data.items[idx].product,
                'options': this.data.items[idx].options
            });
            this.data.items.splice(idx, 1);
        }

        localStorage.removeItem('shippingMethod');
        localStorage.removeItem('shippingCost');
        localStorage.removeItem('deliveryDate');
        localStorage.removeItem('deliveryTime');

        this.data.shippingMethod = null;
        this.data.shippingCost = 0;
        this.data.deliveryDate = null;
        this.data.deliveryTime = null;
        this.data.sconto = null;
        this.data.scontoValue = 0;

        this.calc();
        this.save();

        return report;
    }

    getQuantityOfItemInCart(product : ArticoloFull) {
        let items = this.items.filter(eachItem => {
            return eachItem.product.identificativo_azienda == product.identificativo_azienda &&
                eachItem.product.codart == product.codart;
        });

        let qta_total = 0;

        for (let item of items)
            qta_total += item.quantity;

        for (let item of this.items) {
            if (!item.product.flg_esplosione && item.box_levels && item.box_levels.length) {
                for (let level of item.box_levels) {
                    for (let subItem of level.items) {
                        if (subItem.codice_articolo_componente == product.codart)
                            qta_total += subItem.qta_selezionata * item.quantity;
                    }
                }
            }
        }

        for (let item of this.items){
            if (item.product.attributi && item.product.attributi.length) {
                for (let attr of item.attributi) {
                    if (attr.selected && attr.tipo_battuta == 0 && attr.articolo && attr.articolo_collegato == product.codart)
                        qta_total += 1 * item.quantity;
                }
            }
        }

        return qta_total;
    }

    getQuantityOfItemWithOptions(product: ArticoloFull) {
        let items = this.items.filter(eachItem => {
            return eachItem.product.identificativo_azienda == product.identificativo_azienda &&
                eachItem.product.codart == product.codart;
        });
        let giacenza_opt = [];
        for (let item of items) {
            let giacenza = {};
            const STR_OPT_LIST = "option_list";
            const STR_OPT_CODE = "option_code";
            for (let i = 1; i <= 4; i++) {
                giacenza[STR_OPT_LIST + i] = 0;
                giacenza[STR_OPT_CODE + i] = 0;
                if (item.options[i] && item.options[i].list.indmagazzino) {
                    giacenza[STR_OPT_LIST + i] = item.options[i].list.option_list;
                    giacenza[STR_OPT_CODE + i] = item.options[i].option.progressivo;
                }
            }
            let oldGiacenza = giacenza_opt.find(obj => {
                for (let key of Object.keys(giacenza)) {
                    if (obj[key] != giacenza[key])
                        return false;
                }
                return true;
            });

            if (oldGiacenza)
                oldGiacenza.giacenza += item.quantity;
            else {
                giacenza['giacenza'] = item.quantity;
                giacenza_opt.push(giacenza);
            }
        }
        return giacenza_opt;
    }

    private normalizzaAttributi(attributi?: Attributo[] | null): Attributo[] {
        return [...(attributi || [])]
            .filter(attr => !!attr)
            .sort((a, b) => Number(a.progressivo || 0) - Number(b.progressivo || 0));
    }

    getCartItem(product: ArticoloFull, options?: CartArticoloOption, attributi?: Attributo[]): CartItem {
        let item = this.items.find(eachItem => {
            if (eachItem.product.identificativo_azienda != product.identificativo_azienda)
                return false;
            if (eachItem.product.codart != product.codart)
                return false;

            if (eachItem.options && options) {
                for (let i = 1; i <= 4; i++) {
                    if (eachItem.options[i] && options[i]) {
                        if (eachItem.options[i].list.option_list != options[i].list.option_list)
                            return false;
                        if (eachItem.options[i].option.progressivo != options[i].option.progressivo)
                            return false;
                    }
                    else if (eachItem.options[i] && !options[i] || !eachItem.options[i] && options[i]) {
                        return false;
                    }
                }
            }
            else if (eachItem.options && !options || !eachItem.options && options)
                return false;

            let itemAttributi = this.normalizzaAttributi(eachItem.attributi);
            let attributiPassati = this.normalizzaAttributi(attributi);
            let itemAttributiLength = itemAttributi.length;
            let attributiPassedLength = attributiPassati.length;

            if (itemAttributiLength && attributiPassedLength) {
                if (itemAttributiLength != attributiPassedLength)
                    return false;
                for (let i = 0; i < itemAttributi.length; i++) {
                    if (itemAttributi[i].progressivo != attributiPassati[i].progressivo)
                        return false;
                }
            }
            else if (!itemAttributiLength && attributiPassedLength || itemAttributiLength && !attributiPassedLength)
                return false;

            if (product.conf_active && eachItem.product.conf_active) {
                if (product.conf_active.codice_confezione == eachItem.product.conf_active.codice_confezione &&
                    product.conf_active.pezzi_confezione == eachItem.product.conf_active.pezzi_confezione) {
                    return true;
                }
                else {
                    return false;
                }
            }
            else if (product.conf_active && !eachItem.product.conf_active ||
                !product.conf_active && eachItem.product.conf_active) {
                return false;
            }

            return true;
        });

        return item;
    }

    public recalc() {
        this.save();
        this.calc();
    }

    addSilent(product: ArticoloFull, quantity: number, options?: CartArticoloOption): CartItem {
        let attributiSelezionati = this.normalizzaAttributi(product.attributi ? product.attributi.filter(a => a.selected) : null);
        let item = this.getCartItem(product, options, attributiSelezionati);

        if (item) {
            item.quantity += quantity;
        } else {
            let optionsValues = {};
            if (options) {
                for (let num of Object.keys(options)) {
                    optionsValues[num] = options[num].option.progressivo;
                }
            }
            if (!Object.keys(optionsValues).length)
                optionsValues = null;
            let price = this.priceArticolo.prezzoArticolo(product, optionsValues);
            let attributi = attributiSelezionati.length ? attributiSelezionati : null;
            item = { product, quantity, options, price, attributi};

            this.data.items.push(item);
        }

        return item;
    }

    add(product: ArticoloFull, quantity: number, options?: CartArticoloOption): Observable<CartItem> {
        // timer only for demo
        return timer(0).pipe(map(() => {
            let attributiSelezionati = this.normalizzaAttributi(product.attributi ? product.attributi.filter(a => a.selected) : null);
            let item = this.getCartItem(product, options, attributiSelezionati);

            if (item) {
                item.quantity += quantity;
                this.onAddingSubject$.next({ product, quantity, options, 'price': item.price });
            } else {
                let optionsValues = {};
                if (options) {
                    for (let num of Object.keys(options)) {
                        optionsValues[num] = options[num].option.progressivo;
                    }
                }
                if (!Object.keys(optionsValues).length)
                    optionsValues = null;
                let price = this.priceArticolo.prezzoArticolo(product, optionsValues);
                let attributi = attributiSelezionati.length ? attributiSelezionati : null;
                item = { product, quantity, options, price, attributi};
                this.onAddingSubject$.next({ product, quantity, options, price });
    
                this.data.items.push(item);
            }

            this.save();
            this.calc();

            return item;
        }));
    }

    addBox(product: ArticoloFull, quantity: number, box_levels: Livello[]): Observable<CartItem> {
        // timer only for demo
        return timer(0).pipe(map(() => {
            let price = this.priceArticolo.prezzoArticolo(product);
            if (product.calcolo_prezzo == 1 && !product.flg_esplosione) {
                let totale = 0;
                for (let level of box_levels) {
                  for (let item of level.items) {
                    if (item.qta_selezionata) {
                      let prezzo = this.store.price == 'b2b' ? item.prezzo_netto : item.prezzo_lordo;
                      totale += item.qta_selezionata * prezzo;
                    }
                  }
                }
                price = totale;
            }
            let attributi = this.normalizzaAttributi(product.attributi ? product.attributi.filter(a => a.selected) : null);
            let attributiSelezionati = attributi.length ? attributi : null;
            let item = { product, quantity, box_levels, price, options: null, attributi: attributiSelezionati};
            this.onAddingSubject$.next({ product, quantity, price});

            this.data.items.push(item);

            this.save();
            this.calc();

            return item;
        }));
    }

    update(updates: { item: CartItem, quantity: number }[]): Observable<void> {
        // timer only for demo
        return of(null).pipe(map(() => {
            updates.forEach(update => {
                const item = this.items.find(eachItem => eachItem === update.item);

                if (item) {
                    item.quantity = update.quantity;
                }
            });

            this.save();
            this.calc();
        }));
    }

    remove(item: CartItem, noTimer?): Observable<void> {
        // timer only for demo
        return timer(noTimer ? 0: 1000).pipe(map(() => {
            this.data.items = this.data.items.filter(eachItem => eachItem !== item);

            this.save();
            this.calc();
        }));
    }

    private calc(): void {
        let quantity = 0;
        let subtotal = 0;

        this.data.items.forEach(item => {
            quantity += item.quantity;
            let price = this.itemPriceWithAttributes(item, true) * item.quantity;
            if (price != -1)
                subtotal += price;
        });

        if (this.sconto) {
            if (this.sconto.indicatore_percentuale_assoluto) {
                this.data.scontoValue = Number(this.sconto.valore);
            }
            else {
                this.data.scontoValue = Number(this.sconto.valore) / 100 * (subtotal + this.shippingCost);
            }
        }

        let total = subtotal + this.data.shippingCost - this.data.scontoValue;

        this.calcPayMethodPrice(total);

        total = total + this.data.payMethodPrice;

        this.data.quantity = quantity;
        this.data.subtotal = subtotal;
        this.data.total = total;

        this.itemsSubject$.next(this.data.items);
        this.quantitySubject$.next(this.data.quantity);
        this.subtotalSubject$.next(this.data.subtotal);
        this.totalSubject$.next(this.data.total);
    }
// save all setted items into local storage in JSON format 
    private save(): void {
        localStorage.setItem('cartItems', JSON.stringify(this.data.items));
        localStorage.setItem('destination', JSON.stringify(this.data.destination));
        localStorage.setItem('shippingMethod', JSON.stringify(this.data.shippingMethod));
        localStorage.setItem('shippingCost', JSON.stringify(this.data.shippingCost));
        localStorage.setItem('deliveryDate', JSON.stringify(this.data.deliveryDate));
        localStorage.setItem('deliveryTime', JSON.stringify(this.data.deliveryTime));
        localStorage.setItem('ritiroTime', JSON.stringify(this.data.ritiroTime));
        localStorage.setItem('note', JSON.stringify(this.data.note));
    }
    // load cartItems from localStorage when exist ( converting all data  JSON to OBJ)
    private load(): void {
        const items = localStorage.getItem('cartItems');

        if (items) {
            try {
                this.data.items = JSON.parse(items);
            } catch (err) {
                this.data.items = [];
            }
        }

        const destination = localStorage.getItem('destination');
        if (destination) {
            try {
                this.data.destination = JSON.parse(destination);
            } catch (err) {
                this.data.destination = null;
            }
        }

        const shippingMethod = localStorage.getItem('shippingMethod');
        const shippingCost = localStorage.getItem('shippingCost');
        if (shippingMethod && shippingCost) {
            try {
                this.data.shippingCost = JSON.parse(shippingCost);
                this.data.shippingMethod = JSON.parse(shippingMethod);
            } catch (err) {
                this.data.shippingCost = 0;
                this.data.shippingMethod = null;
            }
        }

        const deliveryDate = localStorage.getItem('deliveryDate');
        const deliveryTime = localStorage.getItem('deliveryTime');
        if (deliveryDate) {
            try {
                this.data.deliveryDate = JSON.parse(deliveryDate);
            } catch (err) {
                this.data.deliveryDate = '';
            }
        }
        if (deliveryTime) {
            try {
                this.data.deliveryTime = JSON.parse(deliveryTime);
            } catch (err) {
                this.data.deliveryTime = '';
            }
        }

        const ritiroTime = localStorage.getItem('ritiroTime');
        if (ritiroTime) {
            try {
                this.data.ritiroTime = JSON.parse(ritiroTime);
            } catch (err) {
                this.data.ritiroTime = '';
            }
        }

        const note = localStorage.getItem('note');
        if (note) {
            try {
                this.data.note = JSON.parse(note);
            } catch (err) {
                this.data.note = '';
            }
        }
    }
    // remove cartItems from localStorage when exist
    resetCart() {
        localStorage.removeItem('cartItems');
        localStorage.removeItem('destination');
        localStorage.removeItem('shippingMethod');
        localStorage.removeItem('shippingCost');
        localStorage.removeItem('deliveryDate');
        localStorage.removeItem('deliveryTime');
        localStorage.removeItem('note');
        localStorage.removeItem('ritiroTime');

        this.data.items = [];
        this.data.shippingMethod = null;
        this.data.shippingCost = 0;
        this.data.destination = null;
        this.data.deliveryDate = null;
        this.data.deliveryTime = null;
        this.data.note = null;
        this.data.sconto = null;
        this.data.scontoValue = 0;
        this.data.ritiroTime = null;

        this.calc();
    }
    // Set ship method using the CartData Interface
    setShippingMethod(shipping, cost) {
        this.data.shippingMethod = shipping;
        this.data.shippingCost = cost;
        this.save();
        this.calc();
    }

    setDestinazione(destinazione) {
        this.data.destination = destinazione;
        this.save();
    }

    setDeliveryDate(deliveryDate) {
        this.data.deliveryDate = deliveryDate;
        this.save();
    }

    setDeliveryTime(deliveryTime) {
        this.data.deliveryTime = deliveryTime;
        this.save();
    }

    setRitiroTime(ritiroTime) {
        this.data.ritiroTime = ritiroTime;
        this.save();
    }

    setNote(note) {
        this.data.note = note;
        this.save();
    }

    setCodiceSconto(sconto : Sconto) {
        this.data.sconto = sconto;
        if (!sconto)
            this.data.scontoValue = 0;
        this.calc();
    }

    getCodiceSconto() {
        return this.data?.sconto ? this.data.sconto.codice : null;
    }

    //by PayCondition verifies if PayMethod Data are correct 
    setPaymentMethod(payMethod : PayCondition) {
        this.data.payMethod = payMethod;
        if (!payMethod)
            this.data.payMethodPrice = 0;
        this.calc();
    }

    itemPriceWithAttributes(item : CartItem, conConf?: boolean) {
        let total = item.price;
        total += this.priceArticolo.priceOfAttributeSelected(item);
        return total;
    }

    creaDocumentoPerSpedizioniDisponibili() {
        // initialize the Obj Model
        let documento : any = {};
        documento['tipoDocumento'] = 'CLI-ORDINE';
        documento["identificativo_azienda"] = this.store.identificativo_azienda;
        documento["prezziIvati"] = this.store.price == 'b2b' ? false : true;
        documento["codiceDestinazione"] = this.destination.codiceDestinazione;

        let corpo = [];
        let i = 0;
        // for each item creates an article Row
        for (let item of this.items) {
            let conf = item.product.conf_active;
            i++;
            let rowArticle = {};
            // populate rowArticle with item-details 
            rowArticle["codiceArticolo"] = item["product"]["codart"];
            rowArticle["descrizioneArticolo"] =  item["product"]["descart"];
            rowArticle["iva"] = item["product"]["aliquota"];
            rowArticle["qta"] = item.quantity;
            if (conf) {
                rowArticle['confezione_articolo'] = {};
                rowArticle['confezione_articolo']['codice_confezione'] = conf.codice_confezione;
            }
            rowArticle["prezzo"] = item.price;
            if (item.options) {
                let opzioni = {};
                for (let num of Object.keys(item.options)) {
                    opzioni["option_list" + num] = item.options[num]['option']['option_list'];
                    opzioni["option_code" + num] = item.options[num]['option']['progressivo'];
                    opzioni["qta"] = item.quantity * (conf ? conf.pezzi_confezione : 1);
                }
                rowArticle['opzioni'] = [opzioni];
            }

            let articolo = item['product'];
            if (articolo.tipo_articolo == 5 || articolo.tipo_articolo == 6) {
                let rows = createCorpoForBox(item, rowArticle, this.store.price, i);
                corpo = corpo.concat(rows);
            }
            else {
                //if attributi item have data push rowArticle and unify rows.
                if (item.attributi) {
                    rowArticle['riferimentoInternoDocumento'] = i + '-ART';
                    corpo.push(rowArticle);
                    // attributiMakeRows crea le righe 
                    let rows = attributiMakeRows(item, rowArticle, i);
                    // unifies rows in 1 Element
                    corpo = corpo.concat(rows);
                }
                else {
                    // load rowArticle al last row
                    corpo.push(rowArticle);
                }
            }
        }

        documento['corpo'] = corpo;
        // stampa documento finito- a cui viene assegnato il corpo, che e il file con i dati inseriti
        return documento;
    }

    creaDocumentoPerCheckout(codeMetodoDiPagamento, fatturaRichiesta) {
        // assegna a documento il documento creato con il metodo soprastante.
        let documento = this.creaDocumentoPerSpedizioniDisponibili();
        //gestisce l'inserimento di tutti i valori necessari per confermare l'ordine
        documento["condizioneDiPagamento"] = codeMetodoDiPagamento;//assegna al pagamento il valore selezionato 
        documento["codice_spedizione"] = this.shippingMethod.codice;// assegna la tipologia di consegna

        if (this.deliveryDate) {
            documento["dataConsegna"] = this.deliveryDate;
            if (this.deliveryTime)
                documento["fascia"] = this.deliveryTime.id;
        }
        if (this.ritiroTime) {
            documento['data_ritiro'] = convertDateRitiroForDocumento(this.ritiroTime);// converte il formato da stringa a Date
        }
        if (this.note)
            documento["note_doc"] = this.note;

        documento["richiesta_fattura"] = fatturaRichiesta ? true : false;

        let corpo = documento['corpo'];

        let subtotal = this.subtotal;
        let sconto: Sconto = this.sconto;

        if (sconto) {
            // verifica se e stato inserito uno sconto al interno del file.
            if (!sconto.indicatore_percentuale_assoluto) {
                corpo.forEach(rowArticle => {
                    if (rowArticle.tipoRiga == 2)
                        return;
                    // se non ce sconto inserisci un array vuoto
                    if (!rowArticle["sconti_maggiorazioni"])
                        rowArticle["sconti_maggiorazioni"] = [];
                    // altrimenti pusha tutti i valori al interno dell'array 
                    rowArticle["sconti_maggiorazioni"].push({
                        "progressivo": 1,
                        "tipo_sconto": 0,
                        "provenienza": sconto.codice,
                        "ind_per_ass": sconto.indicatore_percentuale_assoluto,
                        "valore": sconto.valore
                    });
                });
            }
            else {
                corpo.forEach(rowArticle => {
                    if (rowArticle.tipoRiga == 2)
                        return;
                    if (!rowArticle["sconti_maggiorazioni"])
                        rowArticle["sconti_maggiorazioni"] = [];
                    rowArticle["sconti_maggiorazioni"].push({
                        "progressivo": 1,
                        "tipo_sconto": 0,
                        "provenienza": sconto.codice,
                        "ind_per_ass": sconto.indicatore_percentuale_assoluto,
                        // colcola lo sconto sulla totalità dei prodotti
                        "valore": ((rowArticle.prezzo / subtotal) * Number(sconto.valore)).toFixed(2)
                    });
                })
            }
        }

        corpo.push({
            "codiceArticolo": "",
            "tipoRiga": 4,
            "iva": 22,
            "descrizioneArticolo": getTipoSpedizioneStr(this.shippingMethod.tipo_spedizione),
            "qta": 1,
            "prezzo": this.shippingCost
        });
        // prende il tipo di pagamento selezionato e lo inserisce nel documento 
        if (this.data.payMethodPrice) {
            const rigaPayMethod = this.getPayMethodRiga();
            corpo.push(rigaPayMethod);
        }

        return documento;
    }

    calcPayMethodPrice(totalItemsPrice) {
        if (this.data.payMethod && this.data.payMethod.valore) {
            let payMethod = this.data.payMethod;
            if (payMethod.flag_percentuale_assoluto) {
                this.data.payMethodPrice = payMethod.valore;
            }
            else {
                this.data.payMethodPrice = totalItemsPrice * payMethod.valore / 100;
            }
            if (!payMethod.flag_sconto_maggiorazione) {
                this.data.payMethodPrice = -1 * this.data.payMethodPrice;
            if (this.data.payMethodPrice + totalItemsPrice < 0)
                this.data.payMethodPrice -= (this.data.payMethodPrice + totalItemsPrice);
            }
        }
        else {
            this.data.payMethodPrice = 0;
        }
    }

    formatPayMethodPrice(modPagamento) {
        if (!modPagamento.valore)
            return 'GRATIS';
        let formatPrice = () => {
            if (modPagamento.flag_percentuale_assoluto)
              return modPagamento.valore.toFixed(2) + ' €';
            return modPagamento.valore + '%'
        }
        return (modPagamento.flag_sconto_maggiorazione ? '+' : '-') + formatPrice();
    }

    getPayMethodRiga() {
        if (this.data.payMethodPrice) {
            let descPrezzoModPag = this.data.payMethodPrice > 0 ? "Commissione su mod. pag. " : "Sconto su mod. pag. ";
            descPrezzoModPag += `"${this.data.payMethod.descrizione}" `;
            descPrezzoModPag += `(${this.formatPayMethodPrice(this.data.payMethod)})`;

            return {
                tipoRiga: 3,
                codiceArticolo: '',
                descrizioneArticolo: descPrezzoModPag,
                qta: 1,
                iva: 22,
                prezzo: this.data.payMethodPrice
            }
        }
    }

    principi_articolo;
    setLimitsForShipping(limitiInfo) {
        this.principi_articolo = limitiInfo;
    }

    salvaCarrello() {
        //library that output new content on screen (alert)
        Swal.fire({
            title: 'Salvataggio del carrello',
            html: `<input class="swal2-input" value="Carrello senza nome" placeholder="Nome del carrello" type="text" style="display: flex;">
            <label><input type="checkbox"> Svuotare il carrello</label>
            <div>
                <button id="btn_save_cart" class="btn btn-primary btn-margin">Salva</button>
                <button id="btn_cancel" class="btn btn-secondary btn-margin">Annulla</button>
            </div>`,
            showConfirmButton: false,
            allowEnterKey: false,
            didOpen: (modal) => {
                // @ts-ignore
                modal.querySelector('#btn_save_cart').onclick = (el) => {
                    // @ts-ignore
                    let title = modal.querySelector('input[type="text"]').value;
                    // @ts-ignore
                    let action = modal.querySelector('input[type="checkbox"]').checked ? 'clear' : 'maintain';
                    // @ts-ignore
                    Swal.close({
                        isConfirmed: true,
                        value: { title, action }
                    });
                };

                // @ts-ignore
                modal.querySelector('#btn_cancel').onclick = () => {
                    // @ts-ignore
                    Swal.close({
                        isConfirmed: false,
                        isDismissed: true,
                    });
                }
            },
            customClass: {
                confirmButton: 'btn btn-primary btn-margin',
                denyButton: 'btn btn-secondary btn-margin'
            },
        }).then(r => {
            if (r.isConfirmed) {
                // @ts-ignore
                let title = r.value.title.trim();
                title = title ? title : 'Carrello senza nome';
                this.saveCurrentCart(title);
                this.toastr.success(`Carrello "${title}" è stato salvato`);
                // @ts-ignore
                if (r.value.action == 'clear') {
                    this.setItems([]);
                }
            }
        });

    }

    saveCurrentCart(title) {
        let lastID = Math.max(0, ...this.savedCarts.map(el => el.id));
        lastID += 1;
        // caricamento dei dati carta al interno del oggetto che contiene i dati.
        this.savedCarts.push({
            id: lastID,
            title: title,
            createdAt: localISOTime(new Date()),
            items: JSON.parse(JSON.stringify(this.data.items)),
            quantity: this.data.quantity,
            subtotal: this.data.subtotal,
        });
        // salva tutti i dati al interno del localStorage del utente.
        this.storeSavedCartOfUser();
    }

    restoreSavedCart(savedCart) {
        this.setItems(savedCart.items);
        this.calc();
        this.save();
    }

    removeSavedCart(idCart) {
        this.savedCarts = this.savedCarts.filter(sc => sc.id != idCart);
        this.storeSavedCartOfUser();
    }
// caricamento delle carte dell'utente
    getSavedCart(id) {
        let savedCart = this.savedCarts.find(sc => sc.id == id);
        if (!savedCart)
            return of(null);

        if (savedCart) {
            let resObj = {
                savedCart: [],
                errors: []
            };

            let queries = {};
            for (let item of savedCart.items) {
                queries[item.product.codart] = this.shop.getArticolo(item.product.codart).pipe(
                    catchError(err => of(null))
                );
            }

            if (!Object.keys(queries).length)
                queries['null'] = of(null);
            // return an array of observables 
            return forkJoin(queries).pipe(
                map((res:any) => {
                    let itemsRemoved = savedCart.items.filter(item => !res[item.product.codart]);
                    resObj.errors = itemsRemoved.map(item => `${item.product.codart} - ${item.product.descart}`);
                    for (let item of savedCart.items) {
                        try {
                            let oldImgUrl = item.product.cdn_url;
                            Object.assign(item.product, res[item.product.codart]);
                            item.product.cdn_url = oldImgUrl;
                            let options = {};
                            if (item.options) {
                                for (let i = 1; i <= 4; i++) {
                                    if (item.options[i])
                                        options[i] = item.options[i].option.progressivo;
                                }
                                if (Object.keys(options).length == 0)
                                    options = null;
                            }
                            item.price = this.priceArticolo.prezzoArticolo(res[item.product.codart], options);
                        } catch (err) {}
                    }


                    savedCart.items = savedCart.items.filter(item => res[item.product.codart]);
                    savedCart.items = savedCart.items.filter(item => item.price != -1);
                    savedCart.quantity = savedCart.items.reduce((prev, curr) => prev + curr.quantity, 0);
                    savedCart.subtotal = savedCart.items.reduce((prev, curr) => prev + curr.quantity * curr.price, 0);

                    resObj.savedCart = savedCart;

                    return resObj;
                })
            );
        }
    }

    savedCarts = [];

    loadSavedCartsOnNewUser() {
        this.savedCarts = [];

        try {
            let email = this.account.user$.value.email;
            let savedCarts = JSON.parse(localStorage.getItem('savedCarts'))[email];
            if (savedCarts)
                this.savedCarts = savedCarts;
        } catch (err) {};
    }

    storeSavedCartOfUser() {
        let email = this.account.user$.value.email;
        let allSavedCarts;
        try {
            // prende le carte inserite nel oggetto json
            allSavedCarts = JSON.parse(localStorage.getItem('savedCarts'));
            if (!allSavedCarts)
                throw 'no saved carts';
        } catch (err) {
            allSavedCarts = {};
        }
        //tramite l'email va a prendere le carte dell'utente
        allSavedCarts[email] = this.savedCarts;
        // le salva nel local storage.
        localStorage.setItem('savedCarts', JSON.stringify(allSavedCarts));
    }

    showItemsRemovedError(items) {
        if (!items.length) return;

        let getOptionRowDesc = (item) => {
            if (!item.options)
                return '';

            let optionComponents = [];

            for (let i = 1; i <= 4; i++) {
                let optionItem = item.options[i];
                if (optionItem) {
                    optionComponents.push(`${optionItem.list.descrizione}: ${optionItem.option.codice}`);
                }
            }

            let optionText = optionComponents.map(o => `<li>${o}</li>`).join('');
            if (optionText)
                optionText = `<ul class="cart-table__options" 
                style="display:flex; gap: 10px; justify-content: center">${optionText}</ul>`;
            return optionText;
        }

        let errors = items.map(item => `${item.product.codart} - ${item.product.descart}${getOptionRowDesc(item)}`);
        let html = 'I seguenti articoli non sono più disponibili e sono stati eliminati dal carrello<br><br>';
        html += errors;

        Swal.fire({
            icon: 'warning',
            title: 'Modifica del carrello',
            html: html
        });
    }
    
    isCheckoutAvailable() {
        return this.store.shopConfig['checkout_available']; 
    }
}
